// Noise Analytics (superadmin, while the detector lives in the Lab): a project's proofed files scored by a Lab
// model, each ready to review as soon as it's done.
import { useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { NoiseAnalytics as Data, NoiseFile } from "../api/types";
import { useMe } from "../lib/me";
import { ProjectHeader } from "../components/ProjectHeader";
import { ModelPicker, NOISE_BASE, scanning, startScans } from "../components/NoiseAnalyticsDialog";
import { toast } from "../components/toast";
import { NoiseImportDialog } from "../components/NoiseImportDialog";

const GRAINS = ["Coarse", "Medium", "Fine"] as const;

function Status({ f }: { f: NoiseFile }) {
  const s = f.scan;
  if (!s) return f.why_not ? <span className="muted">{f.why_not[0].toUpperCase() + f.why_not.slice(1)}</span> : <span className="muted">Not scored</span>;
  if (s.status === "queued") return <span className="lab-pill lab-idle">Waiting</span>;
  if (s.status === "running") return <span className="lab-pill lab-extracting">Scoring {Math.round(s.progress)}%</span>;
  if (s.status === "failed") return <><span className="lab-pill lab-failed">Failed</span><div className="lab-err-line" title={s.error ?? ""}>{s.error}</div></>;
  const r = s.review;
  return (
    <>
      <span className="lab-pill lab-done">Scored</span>
      {s.stale ? <div className="lab-err-line">The file was replaced since: score it again</div>
        : (r.judged > 0 || r.flagged > 0) && (
          <div className="muted na-tally">{r.judged} judged · {r.flagged} flagged{r.imported ? ` · ${r.imported} in proofing` : ""}</div>
        )}
    </>
  );
}

export function NoiseAnalytics() {
  const { projectId = "" } = useParams();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string[]>([]);
  const [modelId, setModelId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const anchor = useRef<string | null>(null);        // where a Shift-range starts (a chapter id)
  const q = useQuery({
    queryKey: ["noise", projectId],
    queryFn: () => api<Data>(`${NOISE_BASE}/${projectId}`),
    enabled: !!me?.is_superadmin,
    refetchInterval: (qq) => (qq.state.data?.files.some(scanning) ? 5000 : false),
  });

  if (me && !me.is_superadmin) return <Navigate to={`/projects/${projectId}`} replace />;
  const data = q.data;
  const files = data?.files ?? [];
  const canPick = (f: NoiseFile) => !f.why_not && !scanning(f);
  const pickable = files.filter(canPick);
  const pickedFiles = pickable.filter((f) => picked.includes(f.chapter_id));
  const model = modelId ?? data?.default_model_id ?? "";
  const ready = files.reduce((n, f) => n + (f.scan ? f.scan.review.flagged - f.scan.review.imported : 0), 0);

  /** Ticking as in the Project Hub: a plain click anchors, Shift+click does the same to the rows between. */
  const pick = (index: number, e: ReactMouseEvent<HTMLInputElement>) => {
    const id = files[index].chapter_id;
    const want = !picked.includes(id);
    const from = anchor.current ? files.findIndex((f) => f.chapter_id === anchor.current) : -1;
    setPicked((x) => {
      const n = new Set(x);
      if (e.shiftKey && from >= 0) {
        const [lo, hi] = from < index ? [from, index] : [index, from];
        for (const f of files.slice(lo, hi + 1)) {
          if (!canPick(f)) continue;
          if (want) n.add(f.chapter_id); else n.delete(f.chapter_id);
        }
      } else {
        if (want) n.add(id); else n.delete(id);
        anchor.current = id;
      }
      return [...n];
    });
  };

  async function score() {
    setBusy(true);
    try {
      await startScans(projectId, pickedFiles.map((f) => f.chapter_id), model);
      setPicked([]);
      anchor.current = null;
      qc.invalidateQueries({ queryKey: ["noise", projectId] });
    } catch (e) {
      toast((e as Error).message || "Couldn't start the scoring", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ProjectHeader projectId={projectId} name={data?.project.name ?? "Project"} active="noise"
        right={data?.files.some((f) => f.scan?.status === "done") ? (
          <button className="btn primary sm" onClick={() => setImporting(true)}
                  title="Add the flags you kept or added to this project's proofing">
            Import {ready > 0 ? `${ready} flag${ready === 1 ? "" : "s"} ` : ""}into proofing
          </button>
        ) : undefined} />
      {importing && <NoiseImportDialog projectId={projectId} onClose={() => setImporting(false)} />}
      <div className="page na-page">
        {q.isError ? (
          <div className="lab-error">Couldn't load Noise Analytics: {(q.error as Error).message}</div>
        ) : !data ? (
          <div className="muted">Loading…</div>
        ) : (
          <section className="card">
            <div className="card-head">
              <div className="card-head-title">Files</div>
              <div className="muted lab-add-hint">
                A Lab model flags the noises it hears in each proofed file, about 20 minutes per hour of audio, one file
                at a time. Each file is ready to review as soon as it's scored.
              </div>
            </div>
            <div className="lab-bulk">
              {data.models.length ? <ModelPicker data={data} value={model} onChange={setModelId} /> : <span className="muted">No model yet: upload one on the Noise Lab's Models page.</span>}
              <button className="btn primary sm" disabled={!pickedFiles.length || !model || busy} onClick={score}>
                {busy ? "Starting…" : `Score ${pickedFiles.length ? `${pickedFiles.length} file${pickedFiles.length === 1 ? "" : "s"}` : "files"}`}
              </button>
              {!pickedFiles.length && <span className="muted">Tick proofed files to score them (Shift+click ticks a run of them).</span>}
            </div>
            <div className="lab-tablewrap">
              <table className="table lab-table">
                <thead>
                  <tr>
                    <th className="lab-pickcol">
                      <input type="checkbox" aria-label="Pick every file that can be scored" disabled={!pickable.length}
                             checked={pickable.length > 0 && pickedFiles.length === pickable.length}
                             onChange={(e) => { anchor.current = null; setPicked(e.target.checked ? pickable.map((f) => f.chapter_id) : []); }} />
                    </th>
                    <th>File</th><th>Status</th>
                    {GRAINS.map((g) => <th key={g} className="r">{g}</th>)}
                    <th>Model</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f, i) => (
                    <tr key={f.chapter_id}>
                      <td className="lab-pickcol">
                        {canPick(f) && (
                          <input type="checkbox" checked={picked.includes(f.chapter_id)}
                                 onChange={() => { /* see onClick */ }} onClick={(e) => pick(i, e)}
                                 onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                                 aria-label={`Pick ${f.name}`} />
                        )}
                      </td>
                      <td className="mono na-name">{f.name}</td>
                      <td><Status f={f} /></td>
                      {GRAINS.map((g) => (
                        <td key={g} className="r num">{f.scan?.status === "done" ? (f.scan.grains[g] ?? "—") : "—"}</td>
                      ))}
                      <td className="muted">{f.scan?.model_name ?? ""}</td>
                      <td className="r">
                        {f.scan?.status === "done" && !f.scan.stale && (
                          <Link className="btn sm primary" to={`/projects/${projectId}/noise/${f.chapter_id}`}>Review</Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
